import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicApi, api } from '../lib/api';
import Header from '../components/Header';
import { usePlayer } from '../context/PlayerContext';
import { Play, Shuffle, ChevronLeft, Music2, Headphones } from 'lucide-react';
import HeartButton from '../components/HeartButton';

const formatCount = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

const PlaylistDetail = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const { playQueue, toggleShuffle, shuffle, currentSong } = usePlayer();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      publicApi.getPlaylist(slug),
      api.get(`/playlists/${slug}/related?limit=6`).then((r) => r.data).catch(() => []),
    ]).then(([d, rel]) => {
      setData(d);
      setRelated(rel);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen"><Header /><div className="p-8 text-[#BFA5A5]">Loading...</div></div>;
  if (!data) return <div className="min-h-screen"><Header /><div className="p-8 text-[#BFA5A5]">Playlist not found.</div></div>;

  const { playlist, songs } = data;

  const playAll = () => {
    if (songs.length === 0) return;
    playQueue(songs, 0);
  };

  const playShuffled = () => {
    if (songs.length === 0) return;
    const idx = Math.floor(Math.random() * songs.length);
    if (!shuffle) toggleShuffle();
    playQueue(songs, idx);
  };

  return (
    <div className="min-h-screen pb-32" data-testid={`playlist-page-${slug}`}>
      <Header />
      <main className="max-w-5xl mx-auto">
        {/* Hero cover */}
        <div className="relative h-64 sm:h-80 overflow-hidden">
          <img src={playlist.cover_image} alt={playlist.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#120505] via-[#120505]/60 to-[#120505]/20" />
          <Link
            to="/"
            data-testid="back-link"
            className="absolute top-4 left-4 w-11 h-11 rounded-full bg-[#120505]/70 backdrop-blur flex items-center justify-center hover:bg-[#220A0A]"
          >
            <ChevronLeft className="w-5 h-5 text-[#FFF3E0]" />
          </Link>
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800] mb-2">
              {playlist.category_type === 'era' ? 'Era Playlist' : 'Mood Playlist'}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#FFF3E0] leading-none">
              {playlist.name}
            </h1>
            {playlist.description && (
              <p className="mt-3 text-sm text-[#FFF3E0]/80 max-w-lg font-body">
                {playlist.description}
              </p>
            )}
            <div className="mt-2 text-xs text-[#BFA5A5]">{songs.length} songs</div>
          </div>
        </div>

        {/* Action bar */}
        <div className="px-4 sm:px-6 py-4 flex gap-3 items-center">
          <button
            data-testid="play-all-btn"
            onClick={playAll}
            disabled={songs.length === 0}
            className="h-12 px-6 rounded-full bg-[#FFB800] text-[#120505] font-bold flex items-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-50 shadow-[0_6px_20px_rgba(255,184,0,0.3)]"
          >
            <Play className="w-4 h-4 fill-current" />
            Play
          </button>
          <button
            data-testid="shuffle-play-btn"
            onClick={playShuffled}
            disabled={songs.length === 0}
            className="h-12 w-12 rounded-full border border-[#3D1A1A] flex items-center justify-center hover:border-[#FF007F] transition-colors disabled:opacity-50"
            aria-label="Shuffle play"
          >
            <Shuffle className="w-4 h-4 text-[#FFF3E0]" />
          </button>
          <div className="flex-1" />
          <div className="flex gap-1 flex-wrap justify-end">
            {playlist.tags?.slice(0, 3).map((t) => (
              <span key={t} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border border-[#3D1A1A] text-[#BFA5A5]">
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* Song list */}
        <div className="px-2 sm:px-4">
          {songs.length === 0 ? (
            <div className="text-center py-16 text-[#BFA5A5]">
              <Music2 className="w-8 h-8 mx-auto mb-3 opacity-50" />
              <p>This playlist is empty. Check back soon!</p>
            </div>
          ) : (
            <ul data-testid="song-list">
              {songs.map((song, i) => (
                <li key={song.id}>
                  <div
                    data-testid={`song-row-${i}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => playQueue(songs, i)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        playQueue(songs, i);
                      }
                    }}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[#220A0A] transition-colors text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#FFB800]/40 ${currentSong?.id === song.id ? 'bg-[#220A0A]' : ''}`}
                  >
                    <div className="w-8 text-center text-sm text-[#BFA5A5] font-body">{i + 1}</div>
                    <img src={song.thumbnail_url} alt="" className="w-12 h-12 rounded-md object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className={`truncate text-sm ${currentSong?.id === song.id ? 'text-[#FFB800]' : 'text-[#FFF3E0]'}`}>
                        {song.title}
                      </div>
                      <div className="text-xs text-[#BFA5A5] truncate">{song.artist}</div>
                    </div>
                    {song.play_count > 0 && (
                      <div className="hidden sm:flex items-center gap-1 text-[10px] text-[#BFA5A5] px-2 py-1 rounded-full border border-[#3D1A1A]">
                        <Headphones className="w-3 h-3" />
                        <span className="tabular-nums">{formatCount(song.play_count)}</span>
                      </div>
                    )}
                    <HeartButton song={song} size="sm" testId={`heart-song-${i}`} />
                    <Play className="w-4 h-4 text-[#BFA5A5] opacity-0 group-hover:opacity-100" />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Related playlists */}
        {related.length > 0 && (
          <section className="mt-10 px-4 sm:px-6" data-testid="related-section">
            <div className="flex items-baseline gap-3 mb-4">
              <div className="w-1 h-6 bg-[#FF007F]" />
              <h2 className="font-display text-2xl sm:text-3xl text-[#FFF3E0]">Ye Bhi Suniye</h2>
              <div className="flex-1 h-px bg-[#3D1A1A]" />
            </div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1">
              {related.map((p, i) => (
                <Link
                  key={p.id}
                  to={`/playlist/${p.slug}`}
                  data-testid={`related-playlist-${i}`}
                  className="shrink-0 w-40 sm:w-44 group"
                >
                  <div className="relative w-full aspect-square rounded-2xl overflow-hidden border border-[#3D1A1A]">
                    <img src={p.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#120505] via-[#120505]/40 to-transparent" />
                    <div className="absolute inset-0 p-3 flex flex-col justify-end">
                      <div className="text-[9px] uppercase tracking-[0.2em] text-[#FFB800]">{p.category_type === 'era' ? 'Era' : 'Mood'}</div>
                      <div className="text-sm font-display text-[#FFF3E0] leading-tight drop-shadow-lg line-clamp-2">{p.name}</div>
                      <div className="text-[10px] text-[#FFF3E0]/70 mt-0.5">{p.song_ids?.length || 0} songs</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default PlaylistDetail;
